import * as React from 'react'
import { tw } from 'utils/classnames'

type RootSize = 'large' | 'medium'
function Root({
  children,
  className,
  style,
  size = 'large',
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  size?: RootSize
}) {
  return (
    <div
      style={style}
      className={tw(
        'relative flex flex-col overflow-hidden rounded-3',
        size === 'large' && 'p-6 @3xl:p-12 gap-6',
        size === 'medium' && 'p-6 gap-3',
        className,
      )}
    >
      {children}
    </div>
  )
}

function Background({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <>
      <BackgroundEllipse className={tw('-left-28 -top-20', className)} />
      <BackgroundEllipse className={tw('-bottom-36 -right-36', className)} />

      {children}
    </>
  )
}

function BackgroundEllipse({ className }: { className?: string }) {
  return (
    <div
      className={tw(
        'pointer-events-none absolute size-[400px] rounded-full opacity-70 blur-[145px]',
        className,
      )}
    />
  )
}

function Logo({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={tw('isolate', className)}>{children}</div>
}

/** `none` applies no size classes, so a caller-supplied typography token is not fought by the
 * legacy `typography-24` (which sits later in the stylesheet and would otherwise win). */
type TitleSize = 'medium' | 'large' | 'none'
function Title({
  children,
  className,
  size = 'large',
}: {
  children: React.ReactNode
  className?: string
  size?: TitleSize
}) {
  return (
    <h3
      className={tw(
        'isolate font-poppins font-semibold text-gray-50',
        size === 'large' && 'text-[32px] @3xl:text-[48px] leading-[1.2]',
        size === 'medium' && 'typography-24',
        className,
      )}
    >
      {children}
    </h3>
  )
}

function Description({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={tw('isolate typography-16 max-w-[640px] text-gray-50', className)}>{children}</p>
  )
}

export const PromoCard = {
  Root,
  Background,
  BackgroundEllipse,
  Logo,
  Title,
  Description,
}
